import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';
import { dataManager, DATA_MANAGER_STORE_KEYS } from '../utils/data-manager.js';

// ── How to Play — first-run onboarding overlay (mouse/touch-driven). ──
// Purely additive: a calm, premium glass panel that explains the core loop so new players
// immediately "get it". Mirrors friends-scene / card-shop-scene styling (dark scrim, emerald/gold
// glass, circular close button) and their input contract (`_claimOverlayInput()` to mute the world
// while open + a reliable POINTER_DOWN close). Closes on the close button, the "LET'S PLAY" button,
// a tap on the dim backdrop, or ESC. On dismiss it marks HOW_TO_PLAY_SEEN so it never auto-shows
// again, then resumes/wakes whatever scene launched it. Never touches battle/economy/world logic.

/** The four "steps" of the loop — icon + title + one calm line each. */
const STEPS = [
  {
    icon: '🌿',
    title: 'Catch beasts',
    body: 'Wander the tall grass and walk into a wild beast to battle it. Weaken it, then throw a ball to catch it for your team.',
  },
  {
    icon: '⚔️',
    title: 'Battle & earn Gold',
    body: 'Win battles, scoop up Gold coins on the map, and finish quests. Heal your team for FREE at the Town Center nurse.',
  },
  {
    icon: '🎴',
    title: 'Redeem REAL cards',
    body: 'Spend your Gold in the Card Shop on REAL graded Pokémon cards — shipped to you or sent on-chain. Yes, really.',
  },
  {
    icon: '🎮',
    title: 'Controls',
    body: 'Move with WASD / arrows (or the on-screen joystick on mobile). Interact with SPACE / tap. Tap a nearby player to 💬 DM, ❤️, or ⚔️ challenge them. Open menus from the side icons.',
  },
];

/**
 * @typedef HowToPlaySceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class HowToPlayScene extends BaseScene {
  /** @type {HowToPlaySceneData} */ #sceneData;

  constructor() {
    super({ key: SCENE_KEYS.HOW_TO_PLAY_SCENE });
  }

  /** @param {HowToPlaySceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    // ── Dim scrim — also a tap-away target that closes the overlay. ──
    this.add
      .rectangle(0, 0, width, height, 0x05080d, 0.84)
      .setOrigin(0)
      .setInteractive()
      .on(Phaser.Input.Events.POINTER_DOWN, () => this.#close());

    const panelW = Math.min(720, width - 48);
    const panelH = Math.min(620, height - 40);
    const cx = width / 2;
    const top = height / 2 - panelH / 2;
    const left = cx - panelW / 2;

    // ── Panel (eat clicks so a tap inside doesn't fall through to the scrim). ──
    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.97);
    panel.fillRoundedRect(left, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(left, top, panelW, panelH, 18);
    // gold corner accents (matches card-shop / friends panel polish)
    panel.lineStyle(3, UI.gold, 0.9);
    const a = 22;
    panel.beginPath();
    panel.moveTo(left + 14, top + 14 + a);
    panel.lineTo(left + 14, top + 14);
    panel.lineTo(left + 14 + a, top + 14);
    panel.moveTo(left + panelW - 14 - a, top + panelH - 14);
    panel.lineTo(left + panelW - 14, top + panelH - 14);
    panel.lineTo(left + panelW - 14, top + panelH - 14 - a);
    panel.strokePath();
    this.add
      .zone(left, top, panelW, panelH)
      .setOrigin(0)
      .setInteractive()
      .on(Phaser.Input.Events.POINTER_DOWN, () => {});

    this.add
      .text(cx, top + 42, 'HOW TO PLAY', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    this.add
      .text(cx, top + 74, '🌟  WELCOME TO MONSTARA', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: UI.goldHex,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    // ── The four loop steps, stacked. ──
    const listTop = top + 104;
    const listBottom = top + panelH - 88;
    const rowH = (listBottom - listTop) / STEPS.length;
    const rowPad = 8;
    STEPS.forEach((step, i) => {
      this.#stepRow(step, left + 26, listTop + i * rowH, panelW - 52, rowH - rowPad);
    });

    // ── Primary CTA — close + remember we've seen it. ──
    makeButton(this, {
      x: cx,
      y: top + panelH - 44,
      width: Math.min(360, panelW - 80),
      height: 52,
      label: "LET'S PLAY  ▶",
      primary: true,
      fontSize: 22,
      onClick: () => this.#close(),
    });

    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /**
   * One loop step: a glass card with an emoji badge, a gold title, and a calm body line.
   * @param {{icon:string,title:string,body:string}} step
   */
  #stepRow(step, x, y, w, h) {
    const card = this.add.graphics();
    card.fillStyle(0x0d1c14, 0.85);
    card.fillRoundedRect(x, y, w, h, 12);
    card.lineStyle(2, UI.glassStroke, 0.7);
    card.strokeRoundedRect(x, y, w, h, 12);

    // emoji badge
    this.add
      .text(x + 34, y + h / 2, step.icon, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
        color: '#eafff5',
      })
      .setOrigin(0.5);

    const textX = x + 68;
    const textW = w - 68 - 18;

    this.add
      .text(textX, y + 12, step.title, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: UI.goldHex,
      })
      .setOrigin(0, 0);

    this.add
      .text(textX, y + 38, step.body, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: UI.textHex,
        wordWrap: { width: textW },
        lineSpacing: 2,
      })
      .setOrigin(0, 0);
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  /** Mark the overlay seen (persisted) so it never auto-shows again, then return to the launcher. */
  #close() {
    try {
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.HOW_TO_PLAY_SEEN, true);
      dataManager.saveData();
    } catch (e) {
      // Persisting the flag must never block closing the overlay.
      console.warn('[HowToPlayScene] failed to persist HOW_TO_PLAY_SEEN', e);
    }

    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.HOW_TO_PLAY_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    // ALWAYS release input (handleSceneResume only fires on resume()).
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
